import React, { useEffect, useMemo, useState } from 'react';
import { App, Button, Modal, Select, Skeleton } from 'antd';

import useDashboards from '../../hooks/useDashboards';
import { CHART_SCOPE } from '../../services/charts';
import {
	DASHBOARD_SCOPE, MAX_TILES, dashboardRefusal, getDashboard, updateDashboard, versionConflictOf,
} from '../../services/dashboards';
import { NEW_CHART_TILE, newTileId } from '../../utils/dashboards';
import { normalizeId } from '../../utils/ids';

/** The tiles with the chart placed last, half wide and short (Add chart's tile). */
const withChart = (tiles, chartId) => {
	const list = Array.isArray(tiles) ? tiles : [];
	return [...list, { id: newTileId(list), kind: 'chart', chart_id: chartId, ...NEW_CHART_TILE }];
};

/**
 * Places a chart at the end of a dashboard (charts A6, "Add to dashboard…"):
 * read the dashboard, append a chart tile, and save the whole layout with the
 * version read — the same one write Arrange's Done makes, so a dashboard is
 * never seen half changed. A save that meets someone else's (409
 * `version_conflict`) is made once more over the dashboard as it now is
 * (the 409 carries it); a second refusal is the caller's to say. A full
 * dashboard is refused before anything is sent. → the saved dashboard.
 */
export async function placeChartOnDashboard(collectionId, dashboardId, chartId) {
	const full = new Error(`A dashboard holds at most ${MAX_TILES} tiles. Remove one before adding another.`);
	full.response = { status: 400, data: { code: 'too_many_tiles', message: full.message } };
	let dashboard = await getDashboard(collectionId, dashboardId);
	for (let attempt = 0; attempt < 2; attempt += 1) {
		const tiles = Array.isArray(dashboard?.tiles) ? dashboard.tiles : [];
		if (tiles.length >= MAX_TILES) throw full;
		try {
			// eslint-disable-next-line no-await-in-loop
			return await updateDashboard(collectionId, dashboardId, { tiles: withChart(tiles, chartId), version: dashboard?.version });
		} catch (e) {
			const stale = attempt === 0 ? versionConflictOf(e) : null;
			if (!stale) throw e;
			// eslint-disable-next-line no-await-in-loop
			dashboard = stale.dashboard || await getDashboard(collectionId, dashboardId);
		}
	}
	return dashboard;
}

/** May this chart go on this dashboard (⚑ d08)? A personal chart goes only on a personal dashboard. */
export const dashboardTakes = (dashboard, chart) => Boolean(dashboard && chart)
	&& (dashboard.scope !== DASHBOARD_SCOPE.COLLECTION || chart.scope === CHART_SCOPE.COLLECTION);

/**
 * **Add to dashboard…** (a chart's ⋯, Record group; standards §11): the
 * dashboards of this collection the person may arrange that may hold this
 * chart — a chart of their own only on a dashboard of their own — then the
 * chart placed at the end of the one picked (`placeChartOnDashboard`).
 *
 *   chart         the chart to place
 *   collectionId  the collection whose dashboards are offered (the context)
 *   onPlaced      (dashboard) => void, after the save
 */
export default function AddToDashboardModal({ open, chart, collectionId, onPlaced, onClose }) {
	const { message } = App.useApp();
	const { dashboards, loading, error, reload } = useDashboards(collectionId, { enabled: open });
	const [picked, setPicked] = useState(null);
	const [saving, setSaving] = useState(false);
	const [refusal, setRefusal] = useState(null);
	useEffect(() => {
		if (!open) return;
		setPicked(null);
		setRefusal(null);
		setSaving(false);
	}, [open]);

	const offer = useMemo(() => dashboards.filter((d) => d && d.canEdit === true && dashboardTakes(d, chart)), [dashboards, chart]);
	const options = useMemo(() => {
		const group = (label, list) => (list.length ? [{ label, options: list.map((d) => ({ value: normalizeId(d._id), label: d.name || 'Untitled dashboard' })) }] : []);
		return [
			...group('Pinned for everyone', offer.filter((d) => d.scope === DASHBOARD_SCOPE.COLLECTION)),
			...group('Yours', offer.filter((d) => d.scope !== DASHBOARD_SCOPE.COLLECTION)),
		];
	}, [offer]);

	const add = async () => {
		const target = offer.find((d) => normalizeId(d._id) === picked);
		if (!target || !chart) return;
		setSaving(true);
		setRefusal(null);
		try {
			const saved = await placeChartOnDashboard(collectionId, picked, normalizeId(chart._id));
			message.success(`Added "${chart.name || 'the chart'}" to "${saved?.name || target.name}".`);
			if (typeof onPlaced === 'function') onPlaced(saved || target);
			onClose();
		} catch (e) {
			setRefusal(dashboardRefusal(e, 'Could not add the chart to the dashboard.'));
		} finally {
			setSaving(false);
		}
	};

	let body;
	if (loading && !dashboards.length) body = <Skeleton active paragraph={{ rows: 2 }} title={false} />;
	else if (error) {
		body = (
			<div className='add-chart-modal__error' role='alert'>
				<span>Dashboards couldn't be loaded. Trying again may help.</span>
				<Button size='small' onClick={reload}>Try again</Button>
			</div>
		);
	} else if (!offer.length) {
		body = <p className='add-chart-modal__empty'>No dashboard you can arrange can hold this chart yet. Choose New dashboard on Reports to make one.</p>;
	} else {
		body = (
			<>
				<label className='name-chart-modal__label' htmlFor='add-to-dashboard-select'>Dashboard</label>
				<Select
					id='add-to-dashboard-select'
					className='add-chart-modal__select'
					showSearch
					optionFilterProp='label'
					placeholder='Choose a dashboard'
					value={picked}
					options={options}
					onChange={(value) => { setPicked(value); setRefusal(null); }}
					virtual={false}
				/>
			</>
		);
	}

	return (
		<Modal
			open={open}
			title='Add to dashboard'
			okText='Add'
			onOk={add}
			okButtonProps={{ disabled: !picked }}
			confirmLoading={saving}
			onCancel={onClose}
			destroyOnClose
			className='add-chart-modal add-to-dashboard-modal'
		>
			{chart && chart.scope === CHART_SCOPE.USER ? (
				<p className='name-chart-modal__hint'>This chart is yours, so it goes on dashboards of your own. Pin it for everyone to put it on one for everyone.</p>
			) : null}
			{body}
			{refusal ? <div className='name-chart-modal__error' role='alert'>{refusal}</div> : null}
		</Modal>
	);
}
